/**
 * Shared components for ADatP-3 message views — port of reference
 * `manuals/COP-microservice-integration/ADatP-3 Bridge/adatp3-shared.jsx`.
 *
 * Used by:
 *   - CopAdatp3BridgePage  (full inbox, all message types)
 *   - CopOrdersPage        (read-only operator view)
 *
 * Both pages share the same detail panel — message structure is identical;
 * only the list filtering and header copy differ.
 */
import { useCallback, useEffect, useState } from 'react';
import { formatUtcInTimezone } from '../../lib/time.js';
import { api } from '../../api/index.js';

export function statusBadge(s) {
  if (s === 'ok')      return 'bg-emerald-50 border border-emerald-200 text-emerald-700';
  if (s === 'partial') return 'bg-amber-50 border border-amber-200 text-amber-700';
  if (s === 'failed')  return 'bg-rose-50 border border-rose-200 text-rose-700';
  return 'bg-slate-50 border border-slate-200 text-slate-500';
}

export function MessageDetail({ detail, onClose, isAdmin, onReparse, tz }) {
  const [tab, setTab] = useState('parsed');
  const { entry, parsed, raw } = detail;
  const tabClass = (active) => `px-3 py-1.5 rounded ${active ? 'bg-ink text-white' : 'bg-slate-100 text-ink'}`;
  return (
    <div className="fixed inset-0 z-40 flex items-stretch justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-950/30 backdrop-blur-[2px]" />
      <div className="relative h-full w-full max-w-3xl overflow-y-auto border-l border-line bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 border-b border-line bg-white px-6 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-mono text-xs text-mist">{entry.key}</div>
              <h3 className="mt-0.5 text-lg font-semibold text-ink">{entry.type} from {entry.originator}</h3>
              <div className="mt-0.5 text-xs text-mist">{parsed?.exercise ? `EXER: ${parsed.exercise} · ` : ''}{entry.serial} · {entry.month}/{entry.year}</div>
            </div>
            <button className="text-mist hover:text-ink" onClick={onClose}><i className="fa-sharp fa-solid fa-xmark text-xl" /></button>
          </div>
          <div className="mt-3 flex gap-2 text-sm">
            <button className={tabClass(tab === 'parsed')} onClick={() => setTab('parsed')}>Parsed</button>
            <button className={tabClass(tab === 'raw')}    onClick={() => setTab('raw')}>Raw</button>
            <button className={tabClass(tab === 'acks')}   onClick={() => setTab('acks')}>Acks</button>
            {isAdmin && onReparse && <button className="ml-auto btn-secondary" onClick={onReparse}><i className="fa-sharp fa-solid fa-rotate-right" /> Re-parse</button>}
          </div>
        </div>
        <div className="px-6 py-4">
          {tab === 'parsed' && <ParsedView parsed={parsed} entry={entry} />}
          {tab === 'raw'    && <RawView raw={raw} />}
          {tab === 'acks'   && <AcksView msgKey={entry.key} tz={tz} />}
        </div>
      </div>
    </div>
  );
}

export function ParsedView({ parsed, entry }) {
  if (!parsed) return <div className="text-mist">No parsed data.</div>;
  return (
    <div className="space-y-4 text-sm">
      <Section title="Header">
        <Field k="MSGID" v={`${parsed.msgId?.type} / ${parsed.msgId?.originator} / ${parsed.msgId?.serial} / ${parsed.msgId?.month}/${parsed.msgId?.year}`} />
        {parsed.exercise && <Field k="Exercise" v={parsed.exercise} />}
        {parsed.operation && <Field k="Operation" v={parsed.operation} />}
        {parsed.timezone && <Field k="Timezone" v={parsed.timezone} />}
        {parsed.period && (
          <Field k="Period" v={`${parsed.period.startDtg || '?'} → ${parsed.period.endDtg || '?'}`} />
        )}
        {parsed.qual && <Field k="Classification" v={parsed.qual} />}
      </Section>

      {parsed.refs?.length > 0 && (
        <Section title="References">
          <ul className="list-disc pl-5">
            {parsed.refs.map((r, i) => <li key={i} className="text-mist text-xs">{r.kind}/{r.type} — {r.subject}</li>)}
          </ul>
        </Section>
      )}

      {parsed.amends && (
        <Section title={parsed.amends.action === 'AMNDREF' ? 'Amends' : 'Supersedes'}>
          <Field k={parsed.amends.action} v={parsed.amends.msgId} />
        </Section>
      )}

      <Section title="Body">
        <pre className="overflow-x-auto rounded bg-slate-50 px-3 py-2 text-xs text-ink">{JSON.stringify(parsed.body, null, 2)}</pre>
      </Section>

      {parsed.geometry?.length > 0 && (
        <Section title={`Geometry (${parsed.geometry.length})`}>
          <ul className="space-y-1 text-xs font-mono">
            {parsed.geometry.map((g, i) => (
              <li key={i} className="rounded bg-slate-50 px-2 py-1">
                {g.kind === 'point'
                  ? `point lat=${g.lat?.toFixed(5)} lon=${g.lon?.toFixed(5)} (from ${g.format}: ${g.source})${g.role ? ' · ' + g.role : ''}`
                  : `polygon ${g.coordinates?.length || 0} vertices · ${g.role || ''}`}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {entry.layerObjects?.length > 0 && (
        <Section title="Map drop">
          {entry.layerObjects.map((lo, i) => (
            <div key={i} className="text-xs">layer <code className="font-mono">{lo.layerId}</code> → objects: {lo.objectIds?.length || 0}</div>
          ))}
        </Section>
      )}

      {parsed.parseErrors?.length > 0 && (
        <Section title="Parse errors">
          <ul className="list-disc pl-5 text-rose-700 text-xs">
            {parsed.parseErrors.map((e, i) => <li key={i}>[{e.setId || '?'}] {e.reason}</li>)}
          </ul>
        </Section>
      )}

      {parsed.unknownSets?.length > 0 && (
        <Section title={`Unknown sets (${parsed.unknownSets.length})`}>
          <pre className="overflow-x-auto rounded bg-slate-50 px-3 py-2 text-xs text-mist">{parsed.unknownSets.map((s) => `${s.setId}/${s.fields.join('/')}//`).join('\n')}</pre>
        </Section>
      )}
    </div>
  );
}

export function RawView({ raw }) {
  return <pre className="overflow-x-auto rounded bg-slate-50 px-3 py-2 text-xs font-mono leading-relaxed text-ink">{raw || '(empty)'}</pre>;
}

export function Section({ title, children }) {
  return (
    <div>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-mist">{title}</div>
      <div>{children}</div>
    </div>
  );
}

export function Field({ k, v }) {
  return <div className="text-xs"><span className="font-mono text-mist">{k}</span> <span className="ml-2 text-ink">{v}</span></div>;
}

// ── Delivery confirmations tab ─────────────────────────────────────
// Proxy → ad-tms-service /api/external/orders/:msgKey/acks, enriched
// with /api/external/persons so each row shows callsign + unit; full
// identity lives in the title tooltip.
//
// Graceful degradation:
//   - 503 (no ad-tms-service on this node) → "Data not available"
//   - other failures → red retry banner
export function AcksView({ msgKey, tz }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errKind, setErrKind] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrKind(null);
    try {
      const d = await api.cop.adatp3.acks(msgKey);
      setData(d);
    } catch (err) {
      if (err.status === 503 || err.status === 404 || err.payload?.configured === false) {
        setErrKind('unavailable');
        setData(err.payload || null);
      } else {
        setErrKind(err.message || 'Failed');
      }
    } finally {
      setLoading(false);
    }
  }, [msgKey]);

  useEffect(() => { load(); }, [load]);

  const acks    = Array.isArray(data?.acks) ? data.acks : [];
  const acked   = acks.filter((a) => a.state === 'acked')
                      .sort((a, b) => (b.ackedAt || '').localeCompare(a.ackedAt || ''));
  const refused = acks.filter((a) => a.state === 'refused')
                      .sort((a, b) => (b.ackedAt || '').localeCompare(a.ackedAt || ''));

  return (
    <div className="space-y-4 text-sm">
      <div className="flex items-center gap-3">
        <div className="text-xs">
          <span className="font-semibold text-emerald-700">✅ Acked: {acked.length}</span>
          <span className="mx-2 text-mist">·</span>
          <span className="font-semibold text-rose-700">❌ Refused: {refused.length}</span>
        </div>
        <button
          className="ml-auto btn-secondary !px-2 !py-1 !text-xs"
          onClick={load}
          disabled={loading}
          title="Refresh from ad-tms-service"
        >
          <i className={`fa-sharp fa-solid fa-rotate-right ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {errKind === 'unavailable' && (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-mist">
          Data not available. The local <code>ad-tms-service</code> mesh node is not deployed or its external API isn&apos;t configured.
        </div>
      )}
      {errKind && errKind !== 'unavailable' && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          Failed to load: {errKind}. <button className="underline" onClick={load}>retry</button>
        </div>
      )}

      <Section title="Acked">
        {acked.length === 0
          ? <div className="text-mist text-xs">—</div>
          : acked.map((a, i) => <AckRow key={i} ack={a} tz={tz} positive />)}
      </Section>
      <Section title="Refused">
        {refused.length === 0
          ? <div className="text-mist text-xs">—</div>
          : refused.map((a, i) => <AckRow key={i} ack={a} tz={tz} />)}
      </Section>
    </div>
  );
}

function AckRow({ ack, tz, positive }) {
  const p = ack.recipient;
  const callsign = p?.callsign || `id=${ack.recipientId}`;
  const unitTag  = p?.unit?.name || p?.unit?.shortName || '';
  const tipLines = [];
  if (p) {
    if (p.displayName)   tipLines.push(`Name: ${p.displayName}`);
    if (p.rank)          tipLines.push(`Rank: ${p.rank}`);
    if (p.position)      tipLines.push(`Position: ${p.position}`);
    if (p.unit?.path)    tipLines.push(`Unit: ${p.unit.path}`);
  }
  if (ack.terminalUuid)  tipLines.push(`Terminal: ${ack.terminalUuid}`);
  const tip = tipLines.join('\n');

  return (
    <div
      className={`flex items-center gap-2 rounded px-2 py-1 text-xs ${positive ? 'bg-emerald-50' : 'bg-rose-50'}`}
      title={tip || undefined}
    >
      <span className="font-mono font-semibold text-ink">{callsign}</span>
      {unitTag && <span className="text-mist">/ {unitTag}</span>}
      <span className="ml-auto tabular-nums text-mist">{ack.ackedAt ? formatUtcInTimezone(ack.ackedAt, tz || 'UTC') : '—'}</span>
    </div>
  );
}
